import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Provider } from 'react-redux';
import { createReportBuilderStore } from './redux/builderStore';
import { useAppSelector, RootState } from './redux/builderStoreHooks';
import {
    createBrowserRouter,
    createHashRouter,
    createMemoryRouter,
    createRoutesFromElements,
    Navigate,
    Route,
    RouterProvider,
    useParams
} from 'react-router-dom';
import ProgressMessage from './components/ProgressMessage';
import AppNavigationBar from './components/AppNavigationBar';
import AppFooter from './components/AppFooter';
import UserOptionsSubMenu from './components/UserOptionsSubMenu';

//import PageNavigatorButtons from 'components/PageNavigatorButtons';
import './assets/styles/variables.css';
import './assets/styles/defaults-list-layouts.css';
import './assets/styles/defaults.css';
import './assets/styles/ReportBuilderApp.css';
import './assets/styles/print-defaults.css';

import './assets/styles/index.css';

import { useIntl } from 'react-intl';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { isNullOrUndefined } from './utils/object';
// Import these ones always to avoid script load complexity
import ReportViewIndexPage from './pages/ReportViewIndexPage';
import ReportViewPage from './pages/ReportViewPage';
import { generateGuid } from './utils/report-migrate-plus';
import { generateUserKey } from './utils/storage';
import { IAppConfig, RB_APP_VIEW_ONLY_AUTH_ID } from './utils/appUtilsAndConstants';
import esriId from '@arcgis/core/identity/IdentityManager.js';
import OAuthInfo from '@arcgis/core/identity/OAuthInfo.js';

import '@esri/calcite-components/dist/components/calcite-shell';
import { CalciteShell } from '@esri/calcite-components-react';

// Load these lazily because they make no sense if not launched in "full app" mode
const HomePage = lazy(() => import('./pages/HomePage'));
const AppCannotStartPage = lazy(() => import('./pages/AppCannotStartPage'));
const ReportEditPage = lazy(() => import('./pages/ReportEditPage'));
const ReportPreviewPage = lazy(() => import('./pages/ReportPreviewPage'));
const ReportsComparePage = lazy(() => import('./pages/ReportsComparePage'));
const PreviewComponentsPage = lazy(() => import('./components/PreviewComponentsPage'));
const NoMatchPage = lazy(() => import('./pages/NoMatchPage'));

interface IReportBuilderAppArgs extends IAppConfig {
    id: string;
    authId?: string;
    homeUrl?: string;
    helpUrl?: string;
    portalUrl?: string;
    portalType?: 'online' | 'portal';
    portalHome?: string;
    staticUrl?: string;
    queryString?: string;
    inErrorState?: boolean;
    extraComponents?: any;
    [key: string]: any;
}

type AppWrapperType = {
    props?: IReportBuilderAppArgs;
    signOutOfApp?: () => void;
};

const ReportBuilderApp = (props: IReportBuilderAppArgs) => {
    const {
            id,
            authId,
            homeUrl = 'https://www.reports.esriuk.com/',
            helpUrl = 'https://help.reports.esriuk.com/report-builder/',
            portalUrl = 'https://www.arcgis.com/sharing/rest',
            portalType = 'online',
            portalHome = 'https://www.arcgis.com/',
            staticUrl,
            token: initialToken,
            tokenExpiry,
            user,
            onReady,
            basename,
            queryString,
            appLinks,
            inErrorState,
            routerMode = 'browser',
            className = '',
            extraComponents,
            location: topLocation,
            viewOnlyAuthId = RB_APP_VIEW_ONLY_AUTH_ID,
            featureSwitches,
            ...others
        } = props,
        [approved, setApproved] = useState(false),
        [loading, setLoading] = useState(true),
        scrollTopIcon = useRef<HTMLAnchorElement>(null),
        headerBar = useRef<HTMLDivElement>(null),
        builderStore = useRef(createReportBuilderStore()),
        intl = useIntl();
    //dispatch = useAppDispatch();
    //options = !isNullOrUndefined(appOptions) ? appOptions.find((o) => o.id === 'app') : null;

    useEffect(() => {
        const init = async () => {
            const dispatchable: any = {
                type: 'SET_RB_APP_SETTINGS',
                appId: id ?? '',
                appAuthId: authId ?? '',
                appHome: homeUrl ?? '',
                appHelp: helpUrl,
                portalUrl: portalUrl ?? '',
                portalType: portalType ?? '',
                portalHome: portalHome ?? '',
                staticUrl: staticUrl ?? '',
                token: initialToken ?? '',
                tokenExpiry: !isNullOrUndefined(tokenExpiry) ? tokenExpiry : -1,
                user: user ?? {},
                productHome: 'www.reports.esriuk.com',
                featureSwitches: featureSwitches ?? {}
            };
            builderStore.current.dispatch(dispatchable);
            const oAuthInfo = new OAuthInfo({
                    appId: authId,
                    portalUrl: portalHome
                }),
                registered = [oAuthInfo];
            esriId.registerOAuthInfos(registered);
            esriId.on('dialog-create', () => {
                esriId.dialog.when(() => {
                    // esriId.dialog.content.messages.info = intl.formatMessage({
                    //     id: 'dialog.auth.message',
                    //     defaultMessage: "Please sign in to access data from '{server}'."
                    // });
                });
            });
            // Initial token specified in the URL - this should be used sparingly and generally only for "view" actions
            if (
                initialToken !== undefined &&
                initialToken !== null &&
                initialToken !== '' &&
                esriId !== undefined
            ) {
                esriId.registerToken({
                    server: portalUrl,
                    token: initialToken,
                    expires: !isNullOrUndefined(tokenExpiry)
                        ? tokenExpiry
                        : new Date().getTime() + 30 * 60 * 1000
                });
                // Trim back the token - this is a one time load
                const url = new URL(window.location.href);
                url.searchParams.delete('token');
                url.searchParams.delete('expires');
                window.history.replaceState(window.history.state, '', url.href);
                //console.debug(`⚠ Hard-wired access token: ${initialToken.substring(0, 9)}...`); // DEBUG
            } else {
                // Token?
                try {
                    const tc = await esriId.checkSignInStatus(portalUrl),
                        portalSelf = await fetch(
                            `${portalUrl.replace(/^\/(.*)\/$/, '$1')}/portals/self?f=json&token=${
                                tc.token
                            }`
                        ).then((portalRsp) => {
                            return portalRsp.json();
                        });
                    dispatchable.token = tc.token;
                    dispatchable.tokenExpiry = tc.expires;
                    tc.on('token-change', (te) => {
                        builderStore.current.dispatch({
                            type: 'SET_RB_APP_SETTINGS',
                            token: tc.token,
                            tokenExpiry: tc.expires
                        });
                    });
                    if (portalSelf.error === undefined) {
                        dispatchable.user = portalSelf.user;
                        if (
                            !isNullOrUndefined(portalSelf.urlKey) &&
                            !isNullOrUndefined(portalSelf.customBaseUrl)
                        )
                            dispatchable.portalUrl =
                                'https://' +
                                portalSelf.urlKey +
                                '.' +
                                portalSelf.customBaseUrl +
                                '/sharing/rest/';
                    }
                    builderStore.current.dispatch(dispatchable);
                } catch (tcex) {
                    console.log(tcex);
                }
            }
            if (!isNullOrUndefined(id) && !isNullOrUndefined(initialToken) && initialToken !== '') {
                fetch(portalUrl + 'content/items/' + id + '?f=json&token=' + initialToken).then(
                    (itemRsp) => {
                        itemRsp.json().then((itemInfo) => {
                            const okToGo = !itemInfo.error && itemInfo.id && itemInfo.id === id;
                            setApproved(okToGo);
                            setLoading(false);
                        });
                    }
                );
            }
            window.addEventListener('beforeunload', () => {
                builderStore.current.dispatch({ type: 'CLEAR_PAGE_STATE' });
            });
            if (onReady !== undefined && onReady !== null) {
                onReady(null, {}, {});
            }
        };
        init();
        return () => {};
    }, [
        id,
        authId,
        homeUrl,
        helpUrl,
        portalUrl,
        portalType,
        portalHome,
        staticUrl,
        initialToken,
        tokenExpiry,
        user,
        onReady,
        intl,
        featureSwitches
    ]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver((entries: any[]) => {
            if (entries[0].target.clientWidth) {
                document.documentElement.style.setProperty(
                    '--rb-scrollbar-width',
                    `${window.innerWidth - entries[0].target.clientWidth}px`
                );
            }
        });
        resizeObserver.observe(document.querySelector('main.rb-flex-grow') ?? document.body);
        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const signOutOfApp = () => {
        esriId.destroyCredentials();
        // 1st app load - set some globals (peristed in sessionStorage)
        builderStore.current.dispatch({
            type: 'SET_RB_APP_SETTINGS',
            appId: id || '',
            appAuthId: authId || '',
            portalUrl: portalUrl || '',
            portalType: portalType || '',
            portalHome: portalHome || '',
            token: '',
            tokenExpiry: -1,
            user: null
        });
        // 1st app load - clear some page state (not persisted)
        builderStore.current.dispatch({
            type: 'SET_PAGE_STATE',
            custom: null
        });
        // signOut(initialToken, authId, portalUrl).then(() => {
        window.setTimeout(() => {
            if (window.location.pathname !== (basename || '/'))
                window.location.href = (basename || '/').split('#')[0].split('?')[0]; // +
            // '?action=sign-out&t=' +
            // new Date().getTime().toFixed(0).substring(0, 12);
        }, 50);
        // });
    };

    const appSkinClass = 'normal', //!isNullOrUndefined(options) ? options.skin : 'normal',
        appWrapper = {
            props: { ...props },
            signOutOfApp: signOutOfApp
        },
        landingPageComponent = inErrorState ? AppCannotStartPage : HomePage,
        appUuid = generateGuid(),
        omitLandmarks =
            others.windowMode !== undefined &&
            others.windowMode !== null &&
            others.windowMode === 'embedded',
        appInDev = typeof import.meta.env !== 'undefined' && import.meta.env.DEV === true,
        isTouchDevice = window.matchMedia('(hover: none) and (pointer: coarse)').matches; //ontouchstart' in window || navigator.maxTouchPoints > 0;

    return (
        <DndProvider backend={isTouchDevice ? TouchBackend : HTML5Backend}>
            <Provider store={builderStore.current}>
                <div
                    id={`iaAppRoot${appUuid}`}
                    className={`esriuk-app esriuk-app-body ia-app ia-app-body skin-${appSkinClass} ${className}`}
                    ref={headerBar}
                >
                    <Suspense
                        fallback={
                            <div className="progress-big">
                                <ProgressMessage message="" />
                            </div>
                        }
                    >
                        <AppRouter
                            basename={
                                basename ??
                                (typeof import.meta.env !== 'undefined'
                                    ? import.meta.env.BASE_URL
                                    : '/') ??
                                '/'
                            }
                            queryString={queryString}
                            keyLength={24}
                            mode={routerMode}
                            location={topLocation}
                        >
                            <>
                                <Route
                                    path="/"
                                    element={
                                        <>
                                            <PageWithLayout
                                                component={landingPageComponent}
                                                app={appWrapper}
                                                appLinks={appLinks}
                                                className="home no-header"
                                                homeUrl={homeUrl}
                                                helpUrl={helpUrl}
                                                footer="full"
                                                slottedComponents={extraComponents}
                                                {...others}
                                            />
                                        </>
                                    }
                                />
                                <Route
                                    path="edit-report/:id"
                                    element={
                                        <PageWithLayout
                                            component={ReportEditPage}
                                            app={appWrapper}
                                            appLinks={appLinks}
                                            className="child-page no-header edit-page"
                                            homeUrl={homeUrl}
                                            helpUrl={helpUrl}
                                            minimal={true}
                                            {...others}
                                        />
                                    }
                                />
                                <Route
                                    path="/report/edit/:id"
                                    element={<DynamicRedirect path="/edit-report/:id" />}
                                />
                                <Route
                                    path="/edit-report/:id/:feature"
                                    element={<DynamicRedirect path="/edit-report/:id" />}
                                />
                                <Route
                                    path="/view-local/:id/:feature"
                                    element={
                                        <PageWithLayout
                                            component={ReportViewPage}
                                            app={appWrapper}
                                            appLinks={appLinks}
                                            className="child-page no-header no-nav view-page"
                                            mode="single"
                                            dataMode="local"
                                            terms={false}
                                            landmarks={!omitLandmarks}
                                            homeUrl={homeUrl}
                                            helpUrl={helpUrl}
                                            {...others}
                                        />
                                    }
                                />
                                <Route
                                    path="/view-publication/:id/:feature"
                                    element={
                                        <PageWithLayout
                                            component={ReportViewPage}
                                            app={appWrapper}
                                            appLinks={appLinks}
                                            className="child-page no-header no-nav view-page"
                                            mode="single"
                                            dataMode="static"
                                            terms={false}
                                            landmarks={!omitLandmarks}
                                            homeUrl={homeUrl}
                                            helpUrl={helpUrl}
                                            {...others}
                                        />
                                    }
                                />
                                <Route
                                    path="/view-publication/:id"
                                    element={
                                        <PageWithLayout
                                            component={ReportViewIndexPage}
                                            app={appWrapper}
                                            appLinks={appLinks}
                                            className="child-page no-header no-nav view-page index-page"
                                            dataMode="static"
                                            terms={false}
                                            homeUrl={homeUrl}
                                            helpUrl={helpUrl}
                                            {...others}
                                        />
                                    }
                                />
                                <Route
                                    path="/view-report/:id/aggregate"
                                    element={
                                        <PageWithLayout
                                            component={ReportViewPage}
                                            app={appWrapper}
                                            appLinks={appLinks}
                                            className="child-page no-header no-nav view-page"
                                            mode="aggregate"
                                            terms={false}
                                            appAuthenticationId={viewOnlyAuthId}
                                            homeUrl={homeUrl}
                                            helpUrl={helpUrl}
                                            {...others}
                                        />
                                    }
                                />
                                <Route
                                    path="/view-report/:id/multiple"
                                    element={
                                        <PageWithLayout
                                            component={ReportViewPage}
                                            app={appWrapper}
                                            appLinks={appLinks}
                                            className="child-page no-header no-nav view-page"
                                            mode="aggregate"
                                            terms={false}
                                            appAuthenticationId={viewOnlyAuthId}
                                            homeUrl={homeUrl}
                                            helpUrl={helpUrl}
                                            {...others}
                                        />
                                    }
                                />
                                <Route
                                    path="/view-report/:id/:feature"
                                    element={
                                        <>
                                            {extraComponents}
                                            <PageWithLayout
                                                component={ReportViewPage}
                                                app={appWrapper}
                                                appLinks={appLinks}
                                                className="child-page no-header no-nav view-page"
                                                mode="single"
                                                terms={false}
                                                landmarks={!omitLandmarks}
                                                appAuthenticationId={viewOnlyAuthId}
                                                homeUrl={homeUrl}
                                                helpUrl={helpUrl}
                                                {...others}
                                            />
                                        </>
                                    }
                                />
                                <Route
                                    path="/view-report/:id/:feature/:layer"
                                    element={
                                        <>
                                            {extraComponents}
                                            <PageWithLayout
                                                component={ReportViewPage}
                                                app={appWrapper}
                                                appLinks={appLinks}
                                                className="child-page no-header no-nav view-page"
                                                mode="single"
                                                terms={false}
                                                landmarks={!omitLandmarks}
                                                appAuthenticationId={viewOnlyAuthId}
                                                homeUrl={homeUrl}
                                                helpUrl={helpUrl}
                                                {...others}
                                            />
                                        </>
                                    }
                                />
                                <Route
                                    path="/report/view/:id/:feature"
                                    element={<DynamicRedirect path="/view-report/:id/:feature" />}
                                />
                                <Route
                                    path="/:id/:feature"
                                    element={<DynamicRedirect path="/view-report/:id/:feature" />}
                                />
                                <Route
                                    path="/view-report/:id"
                                    element={
                                        <PageWithLayout
                                            component={ReportViewIndexPage}
                                            app={appWrapper}
                                            appLinks={appLinks}
                                            className="child-page no-header no-nav view-page index-page"
                                            terms={false}
                                            landmarks={!omitLandmarks}
                                            appAuthenticationId={viewOnlyAuthId}
                                            homeUrl={homeUrl}
                                            helpUrl={helpUrl}
                                            {...others}
                                        />
                                    }
                                />
                                <Route
                                    path="/report/view/:id"
                                    element={<DynamicRedirect path="/view-report/:id" />}
                                />
                                <Route
                                    path="/preview-report/:id/:feature"
                                    element={
                                        <PageWithLayout
                                            component={ReportPreviewPage}
                                            app={appWrapper}
                                            appLinks={appLinks}
                                            className="child-page no-header preview-page"
                                            viewer={`${
                                                basename ?? import.meta.env.BASE_URL ?? ''
                                            }/view-report`}
                                            homeUrl={homeUrl}
                                            helpUrl={helpUrl}
                                            {...others}
                                        />
                                    }
                                />
                                <Route
                                    path="/preview-report/:id"
                                    element={
                                        <PageWithLayout
                                            component={ReportPreviewPage}
                                            app={appWrapper}
                                            appLinks={appLinks}
                                            className="child-page no-header preview-page"
                                            viewer={`${
                                                basename ?? import.meta.env.BASE_URL ?? ''
                                            }/view-report`}
                                            homeUrl={homeUrl}
                                            helpUrl={helpUrl}
                                            {...others}
                                        />
                                    }
                                />
                                <Route
                                    path="/compare-reports/:id/:comp"
                                    element={
                                        <PageWithLayout
                                            component={ReportsComparePage}
                                            app={appWrapper}
                                            appLinks={appLinks}
                                            className="child-page no-header preview-page"
                                            viewer={`${
                                                basename ?? import.meta.env.BASE_URL ?? ''
                                            }/view-report`}
                                            homeUrl={homeUrl}
                                            helpUrl={helpUrl}
                                            {...others}
                                        />
                                    }
                                />
                                <Route
                                    path="/embed-report/:id/:feature"
                                    element={
                                        <>
                                            {extraComponents}
                                            <PageWithLayout
                                                component={ReportViewPage}
                                                app={appWrapper}
                                                appLinks={appLinks}
                                                className="child-page no-header no-nav view-page"
                                                mode="single"
                                                navBar={false}
                                                footer="none"
                                                terms={false}
                                                lockNavigation={true}
                                                landmarks={!omitLandmarks}
                                                appAuthenticationId={viewOnlyAuthId}
                                                homeUrl={homeUrl}
                                                helpUrl={helpUrl}
                                                {...others}
                                            />
                                        </>
                                    }
                                />
                                <Route
                                    path="/embed-publication/:id/:feature+"
                                    element={
                                        <PageWithLayout
                                            component={ReportViewPage}
                                            app={appWrapper}
                                            appLinks={appLinks}
                                            className="child-page no-header no-nav view-page"
                                            mode="single"
                                            dataMode="static"
                                            navBar={false}
                                            footer="none"
                                            terms={false}
                                            lockNavigation={true}
                                            landmarks={!omitLandmarks}
                                            homeUrl={homeUrl}
                                            helpUrl={helpUrl}
                                            {...others}
                                        />
                                    }
                                />
                                {appInDev && (
                                    <Route
                                        path="/preview-components"
                                        element={
                                            <PageWithLayout
                                                component={PreviewComponentsPage}
                                                app={appWrapper}
                                                appLinks={appLinks}
                                                className="child-page no-header no-nav view-page"
                                                homeUrl={homeUrl}
                                                helpUrl={helpUrl}
                                            />
                                        }
                                    />
                                )}
                                <Route
                                    path="*"
                                    element={
                                        <PageWithLayout
                                            component={NoMatchPage}
                                            app={appWrapper}
                                            appLinks={appLinks}
                                            className="home no-header"
                                            homeUrl={homeUrl}
                                            helpUrl={helpUrl}
                                            footer="full"
                                        />
                                    }
                                />
                            </>
                        </AppRouter>
                    </Suspense>
                </div>
            </Provider>
        </DndProvider>
    );
};

type AppRouterArgs = {
    basename: string;
    queryString?: string;
    keyLength: number;
    mode?: 'path' | 'browser' | 'hash' | 'static' | 'memory' | 'silent';
    children?: any;
    location?: string;
};

// This should be replaced in the future with redirects as Azure Functions or other server-side technology
const DynamicRedirect = ({ path }: { path: string }) => {
    const p = useParams();
    const to = path.replace(':id', p.id ?? '').replace(':feature', p.feature ?? '');
    return <Navigate to={to} />;
};

const AppRouter = ({
    basename,
    queryString,
    keyLength,
    mode = 'browser',
    children,
    location = '/'
}: AppRouterArgs) => {
    return mode === 'hash' ? (
        <RouterProvider
            router={createHashRouter(createRoutesFromElements(children), { basename })}
            future={{ v7_startTransition: true }}
        />
    ) : mode === 'memory' || mode === 'silent' ? (
        <RouterProvider
            router={createMemoryRouter(createRoutesFromElements(children), {
                basename
            })}
            future={{ v7_startTransition: true }}
        />
    ) : (
        <RouterProvider
            router={createBrowserRouter(createRoutesFromElements(children), {
                basename: basename ?? import.meta.env.BASE_URL ?? ''
            })}
            future={{ v7_startTransition: true }}
        />
    );
};

/* <UserOptions signOutAction={appWrapper.signOutOfApp} showOptions={false} /> */
/* <PageTitle updateDocument={true} /><PageTitle textKey="group" icon="" className="customer-name" /><PageTitle textKey="item" icon="" className="map-name" /> */
const PageWithLayout = ({
    component: Component,
    app: appWrapper = {},
    className: routeClassName,
    footer = 'minimal', // or "full" or "none" or "true" or "false"
    terms = true,
    landmarks = true,
    navBar = true,
    minimal = false, // If minimal, skip most of the layout and let the component do it itself (useful for pages with custom or extended navbars)
    slottedComponents,
    ...rest
}: {
    component: any;
    app?: AppWrapperType;
    className?: string;
    footer?: 'minimal' | 'full' | 'none';
    terms?: boolean;
    landmarks?: boolean;
    navBar?: boolean;
    minimal?: boolean;
    slottedComponents?: any[];
    [id: string]: any;
}) => {
    const rootPath = (import.meta.env.BASE_URL ?? '/').replace(/(.*)\/$/, '$1'),
        activePath = { ...rest }.path || 'error-404',
        rootHelpPath =
            appWrapper.props !== undefined &&
            appWrapper.props.portalType !== undefined &&
            appWrapper.props.portalType.toLowerCase() !== 'online'
                ? `${rootPath}/help/`
                : { ...rest }.helpUrl,
        appHomeUrl = `${
            { ...rest }.homeUrl !== undefined ? { ...rest }.homeUrl : window.location.hostname
        }${rootPath}`.replace(/^(.*)\/$/, '$1'),
        selfLayoutProps = {
            ...rest,
            activePath,
            helpPath: rootHelpPath,
            homeUrl: appHomeUrl,
            controller: appWrapper,
            landmarks,
            navBar,
            footer
        },
        user: any = useAppSelector((state: RootState) => state.appSettings.user),
        userKey = generateUserKey(user);

    const userOptions: any[] = useAppSelector((state: RootState) => state.userOptions) ?? {},
        appFontClassName =
            userOptions.find((o) => o.id === 'application' && o.user === userKey)?.fontSize ??
            'standard',
        appUiThemeName =
            userOptions.find((o) => o.id === 'application' && o.user === userKey)?.uitheme ??
            'calcite-mode-light';

    useEffect(() => {
        document.documentElement.style.setProperty(
            '--esriuk-app-font-size',
            appFontClassName === 'small' ? '15px' : '17px'
        );
        document.documentElement.style.setProperty(
            '--esriuk-app-font-size-small',
            appFontClassName === 'small' ? '13px' : '15px'
        );
    }, [appFontClassName]);

    return (
        <CalciteShell
            className={`page ${appUiThemeName} ${routeClassName} rb-flex rb-flex-col rb-h-screen rb-text-${appFontClassName}`}
        >
            {minimal ? (
                <Component {...rest} {...selfLayoutProps} />
            ) : (
                <>
                    {navBar && (
                        <div className="navbar navbar-fixed-top" slot="header">
                            <AppNavigationBar
                                homeUrl={appHomeUrl}
                                activePath={activePath}
                                controller={appWrapper}
                                helpPath={rootHelpPath}
                                extras={<UserOptionsSubMenu />}
                                userKey={userKey}
                            />
                        </div>
                    )}
                    {!landmarks ? (
                        <div className="app-main">
                            <Component {...rest} />
                        </div>
                    ) : (
                        <main role="main" className="rb-flex-grow rb-relative">
                            <Component {...rest} />
                        </main>
                    )}
                    {footer !== 'none' && (
                        <AppFooter homeUrl={appHomeUrl} content={footer} {...rest} />
                    )}
                    {slottedComponents}
                </>
            )}
        </CalciteShell>
    );
};

export default ReportBuilderApp;
